// Concrete ConversationSource: fetches a Foundry conversation transcript
// by conv-id through the injected OpenAI client. `conversations.items.list`
// returns NEWEST-first (apps/web/lib/gateway/conversations.ts → reverse) so we
// reverse to oldest-first. Transient 500s retry with backoff (spike
// lesson 3); 404 → not-found, 401 → auth (no retry), exhausted → terminal.
// Pure mapping split from the I/O; the OpenAI client is injected (built at the
// entry point from new AIProjectClient(endpoint, credential).getOpenAIClient()).
import { maskSecrets } from "@m8t-stack/agent-ledger";
import type { ConversationSource, NormalizedItem } from "./types";
import { ConvFetchError } from "./types";

/** The narrow slice of the @azure/ai-projects OpenAI client we depend on. */
export interface OpenAILike {
  conversations: {
    items: {
      list(conversationId: string): AsyncIterable<unknown>;
    };
    /** Option A (probe-confirmed, openai@6.37): conversation-level metadata read for the a2a gate. */
    retrieve(conversationId: string): Promise<{ metadata?: unknown }>;
  };
}

interface RawItem {
  type?: string;
  role?: string;
  content?: { text?: string; transcript?: string }[];
  created_at?: number; // Foundry stamps unix-seconds on items
}

const TRANSIENT = 500;
const MAX_ATTEMPTS = 5;

const VOICE_PREFIX = "[[m8t:voice-transcript]] ";
const MAX_VOICE_HEADER_BYTES = 4096;

interface VoiceEnvelope {
  v: 1;
  callId: string;
  startedAt: string;
  endedAt: string;
  turnAt: string;
}

function isVoiceEnvelope(value: unknown): value is VoiceEnvelope {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return item.v === 1 && typeof item.callId === "string" && item.callId.length > 0 &&
    typeof item.startedAt === "string" && item.startedAt.length > 0 &&
    typeof item.endedAt === "string" && item.endedAt.length > 0 &&
    typeof item.turnAt === "string" && item.turnAt.length > 0;
}

/** Returns spoken text only for a completely valid bounded native voice envelope. */
function stripVoiceEnvelope(value: string): string {
  if (!value.startsWith(VOICE_PREFIX)) return value;
  const newline = value.indexOf("\n");
  if (newline < VOICE_PREFIX.length) return value;
  const header = value.slice(VOICE_PREFIX.length, newline);
  if (new TextEncoder().encode(header).byteLength > MAX_VOICE_HEADER_BYTES ||
    header.length % 4 === 1 || !/^[A-Za-z0-9_-]+$/.test(header)) return value;
  try {
    const padded = header.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - header.length % 4) % 4);
    const bytes = Uint8Array.from(Buffer.from(padded, "base64"));
    if (Buffer.from(bytes).toString("base64url") !== header) return value;
    const json = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    const parsed: unknown = JSON.parse(json);
    return isVoiceEnvelope(parsed) ? value.slice(newline + 1) : value;
  } catch {
    return value;
  }
}

function statusOf(e: unknown): number | undefined {
  // `e` is a caught `unknown` (SDK/fetch reject) — may be null/non-object; keep
  // the optional access so a nullish throw doesn't crash the retry classifier.
  const err = e as { statusCode?: number; status?: number } | null | undefined;
  return err?.statusCode ?? err?.status;
}

/** Default backoff: exponential 100ms·2^n, capped at 2s. Injected in tests. */
function defaultSleep(attempt: number): Promise<void> {
  const ms = Math.min(100 * 2 ** attempt, 2000);
  return new Promise((r) => setTimeout(r, ms));
}

function mapItems(raw: RawItem[]): NormalizedItem[] {
  const acc: NormalizedItem[] = [];
  for (const item of raw) {
    if (item.type !== "message") continue;
    if (item.role !== "user" && item.role !== "assistant") continue;
    // Every transcript turn the dream pipeline ever sees passes through here,
    // so secret-shaped strings become [secret] BEFORE any distillation — a
    // brain repo can never memorize a credential, in any deployment. Contact
    // details are NOT masked here: whether a brain may remember emails/phones
    // follows the conversation's owner (the pipeline masks delegated
    // end-user conversations; the install's own team's mates remember).
    // Runs AFTER the voice-envelope strip — the envelope header is base64url
    // and masking it first would break envelope detection.
    const text = Array.isArray(item.content)
      ? maskSecrets(stripVoiceEnvelope(item.content
          .map((c) => typeof c.text === "string" && c.text.length > 0 ? c.text :
            typeof c.transcript === "string" && c.transcript.length > 0 ? c.transcript : "")
          .filter(Boolean)
          .join("\n\n")))
      : "";
    const at =
      typeof item.created_at === "number" ? new Date(item.created_at * 1000).toISOString() : undefined;
    acc.push(at ? { role: item.role, text, at } : { role: item.role, text });
  }
  return acc.reverse(); // SDK is newest-first → oldest-first
}

export function makeConversationSource(
  client: OpenAILike,
  opts: { sleep?: (attempt: number) => Promise<void> } = {},
): ConversationSource {
  const sleep = opts.sleep ?? defaultSleep;
  return {
    async items(conversationId: string): Promise<NormalizedItem[]> {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const raw: RawItem[] = [];
          for await (const it of client.conversations.items.list(conversationId)) {
            raw.push(it as RawItem);
          }
          return mapItems(raw);
        } catch (e) {
          const status = statusOf(e);
          if (status === 404) throw new ConvFetchError("not-found", `conversation ${conversationId} not found`);
          if (status === 401) throw new ConvFetchError("auth", `auth failed reading ${conversationId}`);
          // Only genuine transient (500) or unknown (no status) errors retry until
          // the budget is spent; any other coded status falls straight to terminal.
          const retryable = status === undefined || status === TRANSIENT;
          if (retryable && attempt < MAX_ATTEMPTS - 1) {
            await sleep(attempt);
            continue;
          }
          throw new ConvFetchError(
            "terminal",
            `exhausted ${String(MAX_ATTEMPTS)} retries reading ${conversationId}: ${(e as { message?: string } | null | undefined)?.message ?? String(e)}`,
          );
        }
      }
      // Unreachable (loop either returns or throws), but satisfies the type checker.
      throw new ConvFetchError("terminal", `exhausted retries reading ${conversationId}`);
    },

    /**
     * The a2a recovery gate's data source (Option A). Reads the
     * conversation's own `metadata.app` so the recovery path can HARD-gate on the
     * real app tag rather than emit-time ledger tagging. Degrades to null on any
     * error → the caller treats an un-provable a2a conv as ungated (skipped).
     */
    async retrieveApp(conversationId: string): Promise<string | null> {
      try {
        const conv = await client.conversations.retrieve(conversationId);
        const meta = conv.metadata;
        const app = meta && typeof meta === "object" ? (meta as Record<string, unknown>).app : undefined;
        return typeof app === "string" && app.length > 0 ? app : null;
      } catch {
        return null;
      }
    },
  };
}
